import { useEffect, useState } from "react";
import api from "../lib/api";
import { X, TrendingUp, Loader2, Euro, Package, Building2, Boxes } from "lucide-react";

const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;
const num = (n) => Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });

function Overlay({ title, subtitle, icon: Icon, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm" onClick={onClose} data-testid={testid}>
      <div className="bg-slate-900 border border-slate-700 rounded-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-primary/15 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-primary" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-xl text-slate-50 truncate">{title}</div>
            {subtitle && <div className="text-xs text-slate-400">{subtitle}</div>}
          </div>
          <button data-testid="stats-close" onClick={onClose} className="text-slate-400 hover:text-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, accent }) => (
  <div className="bg-slate-800/60 border border-slate-700 rounded-md px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`font-mono-plex font-bold text-lg ${accent || "text-slate-100"}`}>{value}</div>
  </div>
);

function Bars({ rows, labelKey, valueKey, format }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={r[labelKey] || i} data-testid="stats-bar-row">
          <div className="flex justify-between text-sm mb-0.5">
            <span className="text-slate-200 truncate pr-2">{r[labelKey]}</span>
            <span className="font-mono-plex text-slate-300 shrink-0">{format(r)}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full rounded-full bg-primary" style={{ width: `${((r[valueKey] || 0) / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

const Loading = () => <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>;
const Section = ({ title, children }) => (
  <div className="mt-5">
    <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">{title}</div>
    {children}
  </div>
);

export function ProdottoStatsModal({ id, onClose }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/prodotti/${id}/stats`).then((r) => setS(r.data)).catch(() => setS({ error: true })); }, [id]);
  return (
    <Overlay testid="prodotto-stats-modal" icon={Boxes} onClose={onClose}
      title={s?.prodotto?.nome || "Prodotto"} subtitle={s ? `${s.num_acquisti} acquisti · ${num(s.totale_quantita)} ${s.unita}` : ""}>
      {!s ? <Loading /> : s.error ? <p className="text-slate-400">Errore nel caricamento.</p> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Kpi label="Spesa totale" value={eur(s.totale_spesa)} accent="text-emerald-400" />
            <Kpi label="Quantità totale" value={`${num(s.totale_quantita)} ${s.unita}`} />
            <Kpi label="Prezzo medio" value={s.prezzo_medio != null ? eur(s.prezzo_medio) : "—"} accent="text-primary" />
            <Kpi label="Prezzo min" value={s.prezzo_min != null ? eur(s.prezzo_min) : "—"} />
            <Kpi label="Prezzo max" value={s.prezzo_max != null ? eur(s.prezzo_max) : "—"} />
            <Kpi label="Ultimo prezzo" value={s.ultimo_prezzo != null ? eur(s.ultimo_prezzo) : "—"} />
          </div>
          {s.per_fornitore?.length > 0 && (
            <Section title="Da chi lo compro (spesa per fornitore)">
              <Bars rows={s.per_fornitore} labelKey="fornitore" valueKey="spesa"
                format={(r) => `${eur(r.spesa)} · ${num(r.quantita)} ${s.unita} · med ${r.prezzo_medio != null ? eur(r.prezzo_medio) : "—"}`} />
            </Section>
          )}
          {s.per_mese?.length > 0 && (
            <Section title="Acquisti per mese (spesa)">
              <Bars rows={s.per_mese} labelKey="mese" valueKey="spesa" format={(r) => `${eur(r.spesa)} · ${num(r.quantita)} ${s.unita}`} />
            </Section>
          )}
          {(!s.per_fornitore || s.per_fornitore.length === 0) && (
            <p className="text-slate-500 text-sm mt-5">Ancora nessun acquisto registrato in bolla per questo prodotto.</p>
          )}
        </>
      )}
    </Overlay>
  );
}

export function FornitoreStatsModal({ id, onClose }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/fornitori/${id}/stats`).then((r) => setS(r.data)).catch(() => setS({ error: true })); }, [id]);
  return (
    <Overlay testid="fornitore-stats-modal" icon={Building2} onClose={onClose}
      title={s?.fornitore?.nome || "Fornitore"} subtitle={s ? `${s.num_bolle} bolle · ${s.num_prodotti} prodotti` : ""}>
      {!s ? <Loading /> : s.error ? <p className="text-slate-400">Errore nel caricamento.</p> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Kpi label="Spesa totale" value={eur(s.totale_spesa)} accent="text-emerald-400" />
            <Kpi label="N. bolle" value={num(s.num_bolle)} />
            <Kpi label="Spesa media/bolla" value={eur(s.spesa_media_bolla)} />
            <Kpi label="Ultima bolla" value={s.ultima_bolla || "—"} />
          </div>
          {s.per_prodotto?.length > 0 && (
            <Section title="Cosa compro di più (spesa per prodotto)">
              <Bars rows={s.per_prodotto.slice(0, 15)} labelKey="prodotto" valueKey="spesa"
                format={(r) => `${eur(r.spesa)} · ${num(r.quantita)} · med ${r.prezzo_medio != null ? eur(r.prezzo_medio) : "—"}`} />
            </Section>
          )}
          {s.per_mese?.length > 0 && (
            <Section title="Spesa per mese">
              <Bars rows={s.per_mese} labelKey="mese" valueKey="spesa" format={(r) => `${eur(r.spesa)} · ${r.num_bolle} bolle`} />
            </Section>
          )}
          {s.num_bolle === 0 && <p className="text-slate-500 text-sm mt-5">Ancora nessuna bolla registrata per questo fornitore.</p>}
        </>
      )}
    </Overlay>
  );
}
